/**
 * D-240 / plan §4 A5 — a duration formatted for SPEECH, not for a clock face.
 *
 * The three scrubbers already have a `formatTime` each (v4 `Scrubber`, v3 `Scrubber`,
 * v1 `media_player.formattedTimeLeft`) and all three produce `mm:ss` / `h:mm:ss`. That
 * is the right thing to DISPLAY and the wrong thing to announce: `02:30` is read as
 * "zero two colon three zero" by NVDA and as "two thirty" by VoiceOver, and on a
 * three-hour show `1:05:00` and `1:05` differ only in a colon.
 *
 * 🔑 This does NOT replace those three. They are what the visual cells are recorded
 * against, so touching them is golden churn for no accessibility gain — and the values
 * announced must be the values SHOWN (plan §4 A5's ⚠️: an announced total against a
 * displayed remaining is the same defect A7 fixes in Pagination). Same numbers, said in
 * words; the formatting is the only thing that differs.
 *
 * Zero components are omitted — "5 minutes", not "5 minutes 0 seconds" — because a
 * screen reader reads every word and the zeros carry nothing.
 *
 * @param {number} seconds
 * @return {?string} words, or null when there is no number to say
 */
export default function formatDuration(seconds) {
  // 🪤 NOT `|| 0`. `duration` is NaN until metadata arrives and Infinity on a live
  // stream (v3 `Player.onSeeking` carries the same note for the same reason), and
  // `timeRemaining` can land a hair below zero when currentTime overshoots duration.
  // Announcing "NaN" or "-1 seconds" is worse than announcing nothing: a null here
  // makes the caller OMIT aria-valuetext, and AT then falls back to the percentage,
  // which is at least true.
  if (!Number.isFinite(seconds)) return null;

  const total = Math.max(0, Math.floor(seconds));

  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;

  // Singular matters. "1 minutes remaining" is the tell that a string was assembled
  // rather than written.
  const plural = (value, unit) => `${value} ${unit}${value === 1 ? '' : 's'}`;

  const parts = [];
  if (hours > 0) parts.push(plural(hours, 'hour'));
  if (minutes > 0) parts.push(plural(minutes, 'minute'));
  // The seconds part is dropped only when something LARGER was said, so `0` still
  // renders as "0 seconds" rather than as an empty string.
  if (secs > 0 || parts.length === 0) parts.push(plural(secs, 'second'));

  return parts.join(' ');
}
